import type { Article, Case, Agent, Category } from '@/lib/types';
import { allArticles } from './articles-data';

const categories: Category[] = [
  {
    id: "stable",
    name: "不变",
    slug: "stable",
    type: "article",
    parent_id: null,
    description: "人仍是决策核心，AI只是增强器。战略决策、关键客户关系、组织文化——本质不变，效率提升。",
    icon: "Anchor",
    order: 1,
    created_at: "2026-01-01"
  },
  {
    id: "disrupted",
    name: "颠覆",
    slug: "disrupted",
    type: "article",
    parent_id: null,
    description: "原流程被重构，人从执行者变成指挥者。内容生产、开发、客服、数据分析——岗位减少但能力要求更高。",
    icon: "Zap",
    order: 2,
    created_at: "2026-01-01"
  },
  {
    id: "eliminated",
    name: "删除",
    slug: "eliminated",
    type: "article",
    parent_id: null,
    description: "不再需要人来做。初级客服、数据录入、基础翻译、低端文案——岗位消失，转为系统能力。",
    icon: "Trash2",
    order: 3,
    created_at: "2026-01-01"
  },
  {
    id: "created",
    name: "新增",
    slug: "created",
    type: "article",
    parent_id: null,
    description: "过去不存在的岗位，围绕AI系统运转而生。AI产品经理、Agent设计、数据资产、AI治理——从人力结构到算力结构。",
    icon: "Sparkles",
    order: 4,
    created_at: "2026-01-01"
  }
];

const agents: Agent[] = [
  {
    id: "tool-work-ai",
    name: "工作+AI",
    description: "探索AI如何提升工作效率，获取职场AI应用建议",
    icon: "Briefcase",
    coze_url: "https://yuanqi.tencent.com/webim/#/chat/fctFIU?appid=2046864033288447808&experience=true",
    category_id: "agents",
    tags: ["AI", "职场", "效率"],
    status: "published",
    use_count: 0,
    created_at: "2026-04-23",
    updated_at: null
  },
  {
    id: "tool-what-to-learn",
    name: "你想学什么",
    description: "输入你的需求和背景，获得个性化学习路径推荐",
    icon: "Lightbulb",
    coze_url: "https://yuanqi.tencent.com/webim/#/chat/edQVHy?appid=2044755241453119296&experience=true",
    category_id: "agents",
    tags: ["学习", "AI"],
    status: "published",
    use_count: 0,
    created_at: "2026-04-22",
    updated_at: null
  },
  {
    id: "tool-training-conversion",
    name: "你的课值多少钱",
    description: "评估课程价值，优化定价策略，提升培训转化率",
    icon: "Calculator",
    coze_url: "https://adp.cloud.tencent.com/webim_exp/#/chat/AkOMBx",
    category_id: "agents",
    tags: ["定价", "转化", "课程"],
    status: "published",
    use_count: 186,
    created_at: "2026-02-01",
    updated_at: null
  },
  {
    id: "tool-course-gamification",
    name: "课程游戏化",
    description: "输入课程主题、目标学员，获得游戏化改造方案",
    icon: "Gamepad2",
    coze_url: "https://adp.cloud.tencent.com/webim_exp/#/chat/SCsHPR",
    category_id: "agents",
    tags: ["游戏化", "课程", "设计"],
    status: "published",
    use_count: 142,
    created_at: "2026-02-15",
    updated_at: null
  },
  {
    id: "tool-group-discussion-ai",
    name: "小组讨论AI",
    description: "输入知识点、对象、人数，获得小组讨论方案",
    icon: "Users",
    coze_url: "https://yuanqi.tencent.com/webim/#/chat/SUQMzw?appid=2037027276189895872&experience=true",
    category_id: "agents",
    tags: ["培训", "讨论", "课堂"],
    status: "published",
    use_count: 328,
    created_at: "2026-03-15",
    updated_at: null
  }
];

const cases: Case[] = [];

export async function getMainCategories(): Promise<Category[]> {
  return categories;
}

export async function getSubCategories(parentId: string): Promise<Category[]> {
  return categories.filter(c => c.parent_id === parentId);
}

export async function getCategoryBySlug(slug: string): Promise<Category | null> {
  return categories.find(c => c.slug === slug) || null;
}

export async function getArticles(params?: {
  categoryId?: string;
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<{ articles: Article[]; total: number }> {
  let result = allArticles.filter((a: any) => a && typeof a === 'object');

  if (params?.categoryId) {
    result = result.filter((a: any) => a.category_id === params.categoryId);
  }
  if (params?.status) {
    result = result.filter((a: any) => a.status === params.status);
  }

  result.sort((a: any, b: any) => { const dateDiff = new Date(b.created_at || "2026-01-01").getTime() - new Date(a.created_at || "2026-01-01").getTime(); if (dateDiff !== 0) return dateDiff; return (b.id || "").localeCompare(a.id || ""); });

  const total = result.length;

  if (params?.offset !== undefined && params?.limit) {
    result = result.slice(params.offset, params.offset + params.limit);
  } else if (params?.limit) {
    result = result.slice(0, params.limit);
  }

  return { articles: result, total };
}

export async function getHotArticles(limit: number = 5): Promise<Article[]> {
  return allArticles
    .filter((a: any) => a && a.status === 'published')
    .sort((a: any, b: any) => (b.view_count || 0) - (a.view_count || 0))
    .slice(0, limit);
}

export async function getArticleById(id: string): Promise<Article | null> {
  return allArticles.find((a: any) => a && a.id === id) || null;
}

export async function getAgents(params?: {
  categoryId?: string;
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<{ agents: Agent[]; total: number }> {
  let result = [...agents];

  if (params?.categoryId) {
    result = result.filter(a => a.category_id === params.categoryId);
  }
  if (params?.status) {
    result = result.filter(a => a.status === params.status);
  }

  result.sort((a, b) => b.use_count - a.use_count);

  const total = result.length;

  if (params?.offset !== undefined && params?.limit) {
    result = result.slice(params.offset, params.offset + params.limit);
  } else if (params?.limit) {
    result = result.slice(0, params.limit);
  }

  return { agents: result, total };
}

export async function getHotAgents(limit: number = 5): Promise<Agent[]> {
  return [...agents]
    .filter(a => a.status === 'published')
    .slice(0, limit);
}

export async function getAgentById(id: string): Promise<Agent | null> {
  return agents.find(a => a.id === id) || null;
}

export async function incrementAgentUseCount(id: string): Promise<void> {}

export async function getCases(params?: {
  categoryId?: string;
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<{ cases: Case[]; total: number }> {
  return { cases: [], total: 0 };
}

export async function getHotCases(limit: number = 5): Promise<Case[]> {
  return [];
}

export async function getCaseById(id: string): Promise<Case | null> {
  return null;
}
